import { cn } from "@/lib/cn";

export interface AvatarProps {
  name: string;
  imageUrl?: string | null;
  size?: 20 | 24 | 28 | 36 | 48;
  presence?: "online" | "away" | "offline";
  className?: string;
}

const PX: Record<NonNullable<AvatarProps["size"]>, string> = {
  20: "w-5 h-5 text-[10px]",
  24: "w-6 h-6 text-[11px]",
  28: "w-7 h-7 text-xs",
  36: "w-9 h-9 text-sm",
  48: "w-12 h-12 text-md",
};

/** Deterministic background per name (so seeing avatars = recognising people). */
function colorFor(name: string): string {
  const hues = [
    "bg-brand-100 text-brand-700",
    "bg-success-bg text-green-800",
    "bg-warning-bg text-amber-800",
    "bg-danger-bg text-red-800",
    "bg-surface-raised text-fg-secondary",
  ];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return hues[h % hues.length];
}

function initials(name: string): string {
  if (!name) return "?";
  // For Japanese names, use the first character (CJK char carries info)
  const codepoint = name.codePointAt(0);
  if (codepoint && codepoint > 0x3000) return String.fromCodePoint(codepoint);
  return name
    .split(/\s+/)
    .map((p) => p[0]?.toUpperCase())
    .filter(Boolean)
    .slice(0, 2)
    .join("");
}

const PRESENCE_COLOR = {
  online: "bg-accent-call",
  away: "bg-warning",
  offline: "bg-fg-subtle",
} as const;

export function Avatar({
  name,
  imageUrl,
  size = 28,
  presence,
  className,
}: AvatarProps): JSX.Element {
  return (
    <span
      className={cn(
        "relative inline-flex items-center justify-center rounded-full font-medium overflow-hidden shrink-0",
        PX[size],
        !imageUrl && colorFor(name),
        className,
      )}
      aria-label={name}
      role="img"
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <span aria-hidden="true">{initials(name)}</span>
      )}
      {presence ? (
        <span
          className={cn(
            "absolute bottom-0 right-0 w-2 h-2 rounded-full ring-2 ring-canvas",
            PRESENCE_COLOR[presence],
          )}
          aria-hidden="true"
        />
      ) : null}
    </span>
  );
}
